/**
 * Billing types — invoices, line items, payments.
 *
 * Money is exchanged as JS numbers in the API but stored as
 * numeric(15,2) on the server. Conversion happens at the service
 * boundary so frontend never deals with string decimals.
 *
 * The status of an invoice is computed server-side from payments —
 * the frontend never sets it directly. To "pay" an invoice the UI
 * calls recordPayment; to "cancel" it calls cancelInvoice.
 */
import { z } from "zod";

export const invoiceStatusSchema = z.enum([
	"draft",
	"issued",
	"partially_paid",
	"paid",
	"cancelled",
]);
export type InvoiceStatus = z.infer<typeof invoiceStatusSchema>;

export const paymentMethodSchema = z.enum([
	"cash",
	"card",
	"transfer",
	"online",
	"other",
]);
export type PaymentMethod = z.infer<typeof paymentMethodSchema>;

// ===== Invoice items =====

export const invoiceItemSchema = z.object({
	id: z.string().uuid(),
	invoice_id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	service_id: z.string().uuid().nullable(),
	description: z.string(),
	quantity: z.number(),
	unit_price: z.number(),
	total_price: z.number(),
	sort_order: z.number().int(),
	created_at: z.string().datetime(),
});
export type InvoiceItemApi = z.infer<typeof invoiceItemSchema>;

export const createInvoiceItemInputSchema = z.object({
	description: z.string().min(1).max(500),
	quantity: z.number().positive().max(9999).default(1),
	unit_price: z.number().nonnegative().max(9_999_999),
	/** Optional link to a catalog service — enables revenue-by-service reporting. */
	service_id: z.string().uuid().nullable().optional(),
});
export type CreateInvoiceItemInput = z.infer<
	typeof createInvoiceItemInputSchema
>;

// ===== Payments =====

export const paymentSchema = z.object({
	id: z.string().uuid(),
	invoice_id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	received_by_member_id: z.string().uuid().nullable(),
	// Can be negative for refunds.
	amount: z.number(),
	method: paymentMethodSchema,
	reference: z.string().nullable(),
	notes: z.string().nullable(),
	// Set on refund rows only: the original (positive) payment this refund
	// reverses + the mandatory human reason. Original rows are never mutated.
	refund_of_payment_id: z.string().uuid().nullable(),
	refund_reason: z.string().nullable(),
	received_at: z.string().datetime(),
	created_at: z.string().datetime(),
});
export type PaymentApi = z.infer<typeof paymentSchema>;

export const recordPaymentInputSchema = z.object({
	// Allow negative for refunds; clamp absolute value upstream.
	amount: z
		.number()
		.refine((n) => n !== 0, { message: "Amount cannot be zero" })
		.refine((n) => Math.abs(n) <= 9_999_999, { message: "Amount too large" }),
	method: paymentMethodSchema.default("cash"),
	reference: z.string().max(100).optional(),
	notes: z.string().max(500).optional(),
	received_at: z.string().datetime().optional(),
});
export type RecordPaymentInput = z.infer<typeof recordPaymentInputSchema>;

/**
 * Refund part (or all) of a specific payment. Amount is POSITIVE here —
 * the server stores it as a negative payment row referencing the
 * original. Reason is mandatory: every clinic returns money sometimes,
 * and "why" is the difference between bookkeeping and a leak.
 */
export const refundPaymentInputSchema = z.object({
	amount: z.number().positive().max(9_999_999),
	reason: z.string().trim().min(1).max(500),
	method: paymentMethodSchema.optional(),
	notes: z.string().max(500).optional(),
});
export type RefundPaymentInput = z.infer<typeof refundPaymentInputSchema>;

// ===== Invoice =====

export const invoiceSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	visit_id: z.string().uuid().nullable(),
	issued_by_member_id: z.string().uuid().nullable(),
	invoice_number: z.string(),
	share_token: z.string(),
	status: invoiceStatusSchema,
	subtotal: z.number(),
	discount_amount: z.number(),
	tax_amount: z.number(),
	total_amount: z.number(),
	currency: z.string(),
	notes: z.string().nullable(),
	issued_at: z.string().datetime().nullable(),
	paid_at: z.string().datetime().nullable(),
	cancelled_at: z.string().datetime().nullable(),
	cancellation_reason: z.string().nullable(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),

	/** Computed on read — sum of all payments (negative for refunds). */
	amount_paid: z.number().optional(),
	/** Computed on read — total_amount - amount_paid. */
	balance_due: z.number().optional(),

	/** Eager-loaded items (returned by getInvoice / detail endpoints). */
	items: z.array(invoiceItemSchema).optional(),
	/** Eager-loaded payments. */
	payments: z.array(paymentSchema).optional(),
});
export type InvoiceApi = z.infer<typeof invoiceSchema>;

export const createInvoiceInputSchema = z.object({
	patient_id: z.string().uuid(),
	visit_id: z.string().uuid().optional(),
	/**
	 * When the invoice is issued from inside an appointment (the visit
	 * card) before the doctor saved any visit notes, pass the appointment
	 * id instead of visit_id: the server links the invoice to that
	 * appointment's visit, creating a stub visit record if none exists
	 * yet — so invoices can never be orphaned from their visit context.
	 */
	appointment_id: z.string().uuid().optional(),
	items: z.array(createInvoiceItemInputSchema).min(1).max(50),
	discount_amount: z.number().nonnegative().max(9_999_999).default(0),
	tax_amount: z.number().nonnegative().max(9_999_999).default(0),
	currency: z.string().min(3).max(8).default("IQD"),
	notes: z.string().max(2000).optional(),
	/**
	 * If true, the invoice is created in 'issued' state immediately
	 * (the common case after a visit). If false (default), stays as
	 * 'draft' until explicitly issued via /invoices/:id/issue.
	 */
	issue_immediately: z.boolean().default(true),
});
export type CreateInvoiceInput = z.infer<typeof createInvoiceInputSchema>;

export const cancelInvoiceInputSchema = z.object({
	reason: z.string().max(500).optional(),
});
export type CancelInvoiceInput = z.infer<typeof cancelInvoiceInputSchema>;

export const invoicesListQuerySchema = z.object({
	status: invoiceStatusSchema.optional(),
	patient_id: z.string().uuid().optional(),
	from: z.string().datetime().optional(),
	to: z.string().datetime().optional(),
	page: z.coerce.number().int().positive().default(1),
	per_page: z.coerce.number().int().positive().max(100).default(20),
});
export type InvoicesListQuery = z.infer<typeof invoicesListQuerySchema>;

// ===== Helpers =====

/**
 * Format a money amount with the clinic's currency. Defaults to a
 * conservative locale-free fixed-point so the same value renders the
 * same in ar / en (we let the surrounding layout decide direction).
 */
// ===== Public receipt (patient-facing /receipt/:token) =====

export const publicReceiptSchema = z.object({
	invoice: z.object({
		invoice_number: z.string(),
		status: invoiceStatusSchema,
		subtotal: z.number(),
		discount_amount: z.number(),
		tax_amount: z.number(),
		total_amount: z.number(),
		amount_paid: z.number(),
		balance_due: z.number(),
		currency: z.string(),
		notes: z.string().nullable(),
		issued_at: z.string().datetime().nullable(),
		paid_at: z.string().datetime().nullable(),
		cancelled_at: z.string().datetime().nullable(),
		items: z.array(
			z.object({
				description: z.string(),
				quantity: z.number(),
				unit_price: z.number(),
				total_price: z.number(),
			}),
		),
		payments: z.array(
			z.object({
				amount: z.number(),
				method: paymentMethodSchema,
				received_at: z.string().datetime(),
			}),
		),
	}),
	patient: z.object({
		display_name: z.string(),
		/** The patient's language — the public page opens in it by default. */
		preferred_language: z.enum(["ar", "en", "ckb"]),
	}),
	doctor: z.object({
		display_name: z.string().nullable(),
	}),
	clinic: z.object({
		name: z.object({ ar: z.string(), en: z.string().optional() }),
		slug: z.string(),
		logo_url: z.string().nullable(),
		primary_color: z.string().nullable(),
	}),
});
export type PublicReceiptApi = z.infer<typeof publicReceiptSchema>;

// ===== Revenue summary (dashboard widget) =====

export const revenueWindowSchema = z.object({
	paid: z.number(),
	outstanding: z.number(),
	cancelled_total: z.number(),
	invoice_count: z.number().int(),
	from: z.string().datetime(),
	to: z.string().datetime(),
});
export type RevenueWindow = z.infer<typeof revenueWindowSchema>;

export const revenueSummarySchema = z.object({
	today: revenueWindowSchema,
	this_week: revenueWindowSchema,
	this_month: revenueWindowSchema,
	currency: z.string(),
});
export type RevenueSummary = z.infer<typeof revenueSummarySchema>;

// ===== Commission reporting =====

/**
 * Cash-basis commission report. The commission is computed against
 * payments received within [from, to) — refunds (negative payments)
 * reduce the commission automatically. Cancelled invoices are
 * excluded entirely.
 */
export const commissionRangeQuerySchema = z.object({
	from: z.string().datetime(),
	to: z.string().datetime(),
});
export type CommissionRangeQuery = z.infer<typeof commissionRangeQuerySchema>;

export interface CommissionSummaryRow {
	member_id: string;
	full_name: string;
	role: "owner" | "doctor" | "receptionist" | "accountant" | "nurse";
	/** Snapshot of current commissionPct at report time. Null = 0%. */
	commission_pct: string | null;
	/** SUM(payments.amount) for invoices issued by this member in the window. */
	paid_amount: number;
	/** paid_amount × (commission_pct / 100), rounded to currency. */
	commission_amount: number;
	invoice_count: number;
	payment_count: number;
}

export interface CommissionSummary {
	from: string;
	to: string;
	currency: string;
	rows: CommissionSummaryRow[];
	totals: {
		paid_amount: number;
		commission_amount: number;
	};
}

export interface CommissionDetailRow {
	invoice_id: string;
	invoice_number: string;
	patient_name: string;
	total_amount: number;
	/** Paid amount within the report window (may differ from invoice total). */
	paid_amount_in_window: number;
	commission_amount: number;
	last_payment_at: string | null;
	status: "draft" | "issued" | "partially_paid" | "paid" | "cancelled";
}

export interface CommissionDetail {
	member_id: string;
	full_name: string;
	commission_pct: string | null;
	from: string;
	to: string;
	currency: string;
	rows: CommissionDetailRow[];
	totals: {
		paid_amount: number;
		commission_amount: number;
	};
}

export function formatMoney(amount: number, currency = "IQD"): string {
	// IQD is typically rendered without decimals (Iraqi dinar has no
	// subunits in practice). Other currencies → 2 decimals.
	const fractionDigits = currency === "IQD" ? 0 : 2;
	const rounded =
		Math.round(amount * 10 ** fractionDigits) / 10 ** fractionDigits;
	const withCommas = rounded.toLocaleString("en-US", {
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits,
	});
	return `${withCommas} ${currency}`;
}
